import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './FileDrop.css';

/** Bytes as a short human string: `512 B`, `41.2 KB`, `24.8 MB`. */
export function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Always one decimal: a whole-number "41 KB" silently drops the gap between
  // 41.0 and 41.9, which matters as much at that scale as it does under 10.
  return `${value.toFixed(1)} ${units[unit]}`;
}

/**
 * Whether `file` satisfies an `accept` string.
 *
 * The browser applies `accept` to its own picker, but a dropped file never
 * passes through that dialog — so the rule has to be enforced here as well or
 * dragging a PDF onto the screenshots zone would upload a PDF.
 */
export function matchesAccept(file, accept) {
  if (!accept) return true;

  return accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean)
    .some((rule) => {
      const type = (file.type || '').toLowerCase();
      if (rule.startsWith('.')) return file.name.toLowerCase().endsWith(rule);
      if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
      return type === rule;
    });
}

const isImage = (file) => Boolean(file.type && file.type.startsWith('image/'));

/**
 * One file picker: drag onto it or click to browse, with a preview of what was
 * chosen and a way to take it back off.
 *
 * Controlled — `value` is a `File` (or `null`), or a `File[]` when `multiple`.
 * It hands the caller exactly the values a bare `<input type="file">` would, so
 * the pages' existing FormData code is unaffected by it.
 */
export default function FileDrop({
  label,
  accept,
  multiple,
  value,
  onChange,
  hint,
  required,
}) {
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef(null);
  const [isOver, setIsOver] = useState(false);
  const [skipped, setSkipped] = useState(0);

  const files = useMemo(() => {
    if (multiple) return value || [];
    return value ? [value] : [];
  }, [value, multiple]);

  /*
   * One object URL per previewed image, revoked when the set is replaced or the
   * component unmounts. Skipping the revoke pins each file's bytes in memory
   * for the life of the tab, which on a ten-screenshot upload is real.
   */
  const previews = useMemo(
    () => files.map((file) => (isImage(file) ? URL.createObjectURL(file) : null)),
    [files]
  );

  useEffect(
    () => () => previews.forEach((url) => url && URL.revokeObjectURL(url)),
    [previews]
  );

  function accept_(incoming) {
    const list = Array.from(incoming || []);
    if (!list.length) return;

    const allowed = list.filter((file) => matchesAccept(file, accept));
    setSkipped(list.length - allowed.length);
    if (!allowed.length) return;

    onChange(multiple ? [...files, ...allowed] : allowed[0]);
  }

  function removeAt(index) {
    onChange(multiple ? files.filter((_, i) => i !== index) : null);
    setSkipped(0);
    // Without this, re-picking the same file fires no change event.
    if (inputRef.current) inputRef.current.value = '';
  }

  function onDrop(event) {
    event.preventDefault();
    setIsOver(false);
    accept_(event.dataTransfer && event.dataTransfer.files);
  }

  const showsZone = multiple || files.length === 0;

  return (
    <div className="itcFileDrop">
      <label className="itcFileDropLabel" htmlFor={inputId}>
        {label}
        {required && (
          <span className="itcFileDropRequired" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <input
        ref={inputRef}
        id={inputId}
        className="itcFileDropInput"
        type="file"
        /*
         * The native attribute is a dialog affordance only — it makes the OS
         * picker default to matching files. It is not enforcement: browsers let
         * the user switch the dialog to "All Files", and a dropped file never
         * passes through the dialog at all. accept_() below is the real guard,
         * and it runs on both paths.
         */
        accept={accept}
        multiple={multiple}
        aria-describedby={skipped > 0 ? errorId : undefined}
        onChange={(event) => accept_(event.target.files)}
      />

      {showsZone && (
        // A `<label htmlFor>` here would concatenate with the visible field
        // label above under the HTML label-association algorithm, giving the
        // input a computed accessible name of "App Logo Drop a file here, or
        // browse" instead of just "App Logo". Opening the picker
        // programmatically keeps the input's accessible name singular.
        <div
          data-testid="filedrop-zone"
          className={`itcFileDropZone${isOver ? ' itcFileDropZoneOver' : ''}`}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragEnter={(event) => {
            event.preventDefault();
            setIsOver(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setIsOver(false)}
          onDrop={onDrop}
        >
          <span className="itcFileDropIcon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </span>
          <span className="itcFileDropPrompt">
            Drop {multiple ? 'files' : 'a file'} here, or <u>browse</u>
          </span>
          {hint && <span className="itcFileDropHint">{hint}</span>}
        </div>
      )}

      {files.length > 0 && (
        <ul className={`itcFileDropList${multiple ? ' itcFileDropGrid' : ''}`}>
          {files.map((file, index) => (
            <li className="itcFileDropItem" key={`${file.name}-${index}`}>
              {previews[index] ? (
                <img className="itcFileDropThumb" src={previews[index]} alt={`${file.name} preview`} />
              ) : (
                <span className="itcFileDropBadge" aria-hidden="true">
                  {(file.name.split('.').pop() || 'file').slice(0, 4).toUpperCase()}
                </span>
              )}

              <span className="itcFileDropMeta">
                <span className="itcFileDropName">{file.name}</span>
                <span className="itcFileDropSize">{formatSize(file.size)}</span>
              </span>

              <button
                type="button"
                className="itcFileDropRemove"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${file.name}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      {skipped > 0 && (
        <p className="itcFileDropError" id={errorId} role="alert">
          {skipped} file{skipped === 1 ? '' : 's'} skipped — wrong file type for this field
        </p>
      )}
    </div>
  );
}

FileDrop.propTypes = {
  label: PropTypes.string.isRequired,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  hint: PropTypes.string,
  required: PropTypes.bool,
};
